import { useState, useEffect, useRef, MutableRefObject } from 'react';
import {
  Button,
  ButtonSet,
  Tooltip,
  ContentSwitcher,
  Switch,
  ModalWrapper,
  InlineNotification,
  NumberInput,
} from 'carbon-components-react';
import { FormattedMessage } from 'react-intl';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';
import './InstanaPriceEstimator.scss';
import './NoteDrawer.scss';
import 'src/common/style/cds-data.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import {
  segmentTracking,
  productTitle,
  pageUrl,
} from 'src/apps/instana-price-estimator/lib/tracker';
import Thanks from 'src/apps/instana-price-estimator/components/Thanks';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import * as URX_FORM from 'src/apps/instana-price-estimator/constants/urx-form';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import {
  IQMHosts,
  limits,
  Product,
  APMHosts,
  UT30InstanaPE,
  emailCOSJson,
} from 'src/apps/instana-price-estimator/constants/defaults';
import messages from 'src/apps/instana-price-estimator/locales/messages';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { setPrice } from 'src/apps/instana-price-estimator/hooks/usePrice';

import {
  determineCountry,
  LangDefaults,
  useLang,
  getMessage,
} from 'src/common/hooks/widgets/useLang';
import countryList from '../data/currencies.json';
import Footnote from './Footnote';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import { useTimer } from 'src/common/hooks/useTimer';
import Feedback from 'src/common/utils/Feedback';
import { FormModal } from 'src/common/utils/FormModal';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';

export interface InstanaPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

export interface roiCookieContent {
  disruptions: number;
  preventions: number;
  speedGain: number;
  avgTime: string;
  itOpsEmployees: number;
  developers: number;
  developerEffiencyGains: string;
  itOpsEffiencyGains: string;
  openSourceGains: string;
  totalEffiencyGains: string;
  openSourceTechnoList: string;
}

export const cookieContentObject: roiCookieContent = {
  disruptions: 0,
  preventions: 0,
  speedGain: 0,
  avgTime: '',
  itOpsEmployees: 0,
  developers: 0,
  developerEffiencyGains: '',
  itOpsEffiencyGains: '',
  openSourceGains: '',
  totalEffiencyGains: '',
  openSourceTechnoList: '',
};

/**
 *
 * @param {*} param0
 * @returns
 */
const InstanaPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: InstanaPriceEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [iqmHosts, setIQMHosts] = useState(IQMHosts.default);
  const [apmHosts, setAPMHosts] = useState(APMHosts.default);
  const [cookieContent, setCookieContent] = useState(cookieContentObject);
  const [showContactUs, setShowContactUs] = useState(true);
  const [invalidAPMHostsMax, setInvalidAPMHostsMax] = useState(false);
  const [invalidAPMHostsOptimal, setInvalidAPMHostsOptimal] = useState(false);
  const [invalidAPMHostsMin, setInvalidAPMHostsMin] = useState(false);
  const [invalidIQMHostsMax, setInvalidIQMHostsMax] = useState(false);
  const [invalidIQMHostsMin, setInvalidCIMin] = useState(false);
  const [invalidIQMHostsOptimal, setInvalidIQHostOptimal] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const [environment] = useEnv();
  const { bpName, companyId, logo, desc } = useBPName(UT30InstanaPE, true);
  const cookieURL = useCookiesURL();
  const [productToAcquire, setProductToAcquire] = useState('SaaS');
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  const [calculate, setCalculate] = useState(true);
  const [closeModal, setCloseModal] = useState(false);
  const apmHostsRef = useRef() as MutableRefObject<HTMLInputElement>;
  const iqmHostsRef = useRef() as MutableRefObject<HTMLInputElement>;
  //
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  // Set us-en
  const [urxlang, setUrxLang] = useState(
    LangDefaults.localCountryLang as IBMLocale,
  );
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  // Set USD
  const [modalLocalCurrency, setModalLocalCurrency] = useState(
    LangDefaults.localCurrency,
  );
  // Set English language
  const [modalLocalLanguage, setModalLocalLanguage] = useState(
    LangDefaults.CountryLanguage,
  );
  const [estimatedPrice, setEstimatedPrice] = useCurrency(
    currency, // Showing prices in USD, as per change INSTANA-103
    userLocale,
    0,
  );

  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
    setCloseModal(true);
  };

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const [showForm, setShowForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formId, setFormId] = useState('');

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
  );

  /**
   * Call to start the pricing
   */
  setPrice(
    iqmHosts,
    apmHosts,
    setShowContactUs,
    setEstimatedPrice,
    productToAcquire,
    currency,
    country,
    userLocale,
    calculate,
  );

  /**
   * Define validation of the Input field of the sliders
   */
  useEffect(() => {
    const apmHostsId = document.getElementById('APMHosts-Id');
    const iqmHostsId = document.getElementById('IQMHosts-Id');

    const addListener = (
      id: HTMLElement | null,
      setMax: React.Dispatch<React.SetStateAction<boolean>>,
      setMin: React.Dispatch<React.SetStateAction<boolean>>,
      setOptimal: React.Dispatch<React.SetStateAction<boolean>>,
      max: number,
      min: number,
      optimal: number,
      setValue: React.Dispatch<React.SetStateAction<number>>,
    ) => {
      if (id === null) {
        return;
      }
      id.addEventListener('focusout', (e) => {
        if (e == null) {
          return;
        }
        const element = e.currentTarget as HTMLInputElement;
        if (element.value === '') {
          setValue(min);
          return;
        }
        const valueNumber = Number.parseInt(element.value);
        if (valueNumber > max) {
          element.value = max.toString();
          setValue(max);
          setMax(true);
          return;
        }
        if (valueNumber < min) {
          element.value = min.toString();
          setValue(min);
          setMin(true);
          return;
        }
        if (valueNumber < optimal && valueNumber > min) {
          element.value = optimal.toString();
          setValue(optimal);
          setOptimal(true);
          return;
        }
      });
    };

    /**
     * APMHosts Instances
     */
    addListener(
      apmHostsId,
      setInvalidAPMHostsMax,
      setInvalidAPMHostsMin,
      setInvalidAPMHostsOptimal,
      APMHosts.max,
      APMHosts.min,
      APMHosts.optimal,
      setAPMHosts,
    );

    /**
     * IQMHosts Instances
     */
    addListener(
      iqmHostsId,
      setInvalidAPMHostsMax,
      setInvalidCIMin,
      setInvalidIQHostOptimal,
      IQMHosts.max,
      IQMHosts.min,
      IQMHosts.optimal,
      setIQMHosts,
    );
  }, []);

  /**
   * Read and set defaults from ROI
   */
  const startTxnProcessor = () => {
    try {
      setTimeout(async () => {
        const showBenefit = localStorage.getItem('showInstanaForm');
        if (showBenefit === 'true') {
          setShowForm(true);
        }
        const showContactForm = localStorage.getItem('showContactForm');
        if (showContactForm === 'true') {
          setShowContactForm(true);
        }
        startTxnProcessor();
      }, limits.secs * 1000);
    } catch (err) {
      console.error(`Error while timeout: ${err}`);
    }
  };

  startTxnProcessor();

  /**
   * Show BVA form
   */
  useEffect(() => {
    const showContactForm = localStorage.getItem('showContactForm');
    if (showContactForm === 'true') {
      setFormId(URX_FORM.FORM_ID);
      initUrxForm(
        environment,
        URX_FORM.INSTANCE_ID,
        URX_FORM.FORM_ID,
        urxlang,
        companyId,
      );
      setTimeout(
        async () => {
          setCookie(URX_FORM.FORM_ID);
          setDrawerOpen(true);
          setDrawerOpen(true);
          setShowContactForm(false);
          localStorage.setItem('showContactForm', 'false');
        },
        bpName ? 1000 : 10,
      );
    }
    if (showForm) {
      const showBenefit = localStorage.getItem('showInstanaForm');
      if (showBenefit !== null) {
        if (showBenefit === 'true') {
          setFormId(URX_FORM.FORM_ID_BVA);
          initUrxForm(
            environment,
            URX_FORM.INSTANCE_ID,
            URX_FORM.FORM_ID_BVA,
            urxlang as IBMLocale,
            companyId,
          );
          setCookie(URX_FORM.FORM_ID_BVA);
          setDrawerOpen(true);
          setShowForm(false);
          localStorage.setItem('showInstanaForm', 'false');
        }
      }
    }
  }, [showForm, showContactForm]);

  const { timeout, resetTimer } = useTimer(false, 120000);

  useEffect(() => {
    if (timeout) {
      setFormId(URX_FORM.FORM_ID);
      initUrxForm(
        environment,
        URX_FORM.INSTANCE_ID,
        URX_FORM.FORM_ID,
        urxlang,
        companyId,
      );
      setCookie(URX_FORM.FORM_ID);
      setDrawerOpen(true);
    }
  }, [timeout]);

  useEffect(() => {
    localStorage.setItem('APMHosts-Id', apmHosts.toString());
    localStorage.setItem('IQMHosts-Id', iqmHosts.toString());
  }, [apmHosts, iqmHosts, estimatedPrice]);

  const setCookie = (formId: string) => {
    const jsonCookie = localStorage.getItem('InstanaFormContent');
    if (jsonCookie !== null) {
      const jsonFileToProcess = JSON.parse(jsonCookie);
      setCookieContent(jsonFileToProcess.content);
    }
    const text = JSON.stringify({
      source: formId,
      // One field to provide all values (stringify)
      dcq: {
        Q_NOTESDATA:
          cookieURL +
          ' | Number Of APM Hosts:' +
          apmHosts +
          ' | Number of IQM Hosts: ' +
          iqmHosts +
          ' | Product to acquire: ' +
          productToAcquire +
          ' | Estimated price: ' +
          estimatedPrice +
          ' | CTA -IBM Instana Estimator-' +
          ' | FormId:' +
          formId,
        Q_ASSET_NAME: 'IBM Instana Estimator', // ISC - Asset Name
      },
    });
    Cookies.set('urxdcq', text);
  };

  /* Have urx form deault in us-en when on language us page */
  useEffect(() => {
    const fetchedUrxLang = localeCode; // us-en
    setUrxLang(userLocale as IBMLocale); // de-de
    if (fetchedUrxLang !== lang) {
      setUrxLang(fetchedUrxLang as IBMLocale); // default us-en
    }
  });

  /**
   *
   * @param value
   * @param setValue
   * @param max
   * @param min
   */
  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    min: number,
    optimal: number,
  ) => {
    resetTimer();
    if (value >= min && value <= max) {
      setValue(value < optimal && value > min ? optimal : value);
    } else if (value >= max) {
      setValue(max);
    } else {
      setValue(min);
    }
  };

  return (
    <section id="Instana_PE" className="widget-styles">
      <div className="Instana_PE dsh-widget widget-reset">
        <Helmet>
          <script src={urxScript}></script>
          <script src={urxScriptLoader}></script>
        </Helmet>
        {formComplete ? (
          <Thanks
            iqmHosts={iqmHosts}
            apmHosts={apmHosts}
            productToAcquire={productToAcquire}
            estimatedPrice={estimatedPrice}
            cookieContent={cookieContent}
            bpName={bpName}
            bpConsentMail={false}
          ></Thanks>
        ) : (
          // Not Form completed
          <div className="bx--grid" id="InstanaGrid">
            {bpName ? (
              <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
            ) : null}
            <div className="bx--row mar3">
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                <div className="bx--row">
                  <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12">
                    {localExist && toggleCountry ? (
                      <ModalWrapper
                        modalLabel={` ${getMessage(
                          messages.foundCountry,
                        )} ${modalLocalCountry}.
                      ${getMessage(
                        messages.accessPage,
                      )} ${modalLocalLanguage} ${modalLocalCurrency}
                      `}
                        modalHeading={`${getMessage(messages.availableIn)}
                      ${modalLocalLanguage} ${getMessage(
                          messages.localCurrencie,
                        )} ${modalLocalCurrency}`}
                        size={'sm'}
                        primaryButtonText={`${getMessage(
                          messages.takeMeThere,
                        )}`}
                        secondaryButtonText={`${getMessage(
                          messages.stayHere,
                        )} `}
                        buttonTriggerText={` ${getMessage(
                          messages.accessPageCapitalized,
                        )} ${modalLocalLanguage}`}
                        handleOpen={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'Instana Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Modal open ' + modalLocalCountry,
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        handleSubmit={() => {
                          localStorage.setItem('langLocale', selectedLocale);
                          (window.location.href =
                            'https://www.ibm.com/' +
                            selectedLocale +
                            '/products/instana/pricing'),
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Instana Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Switched to country ' + modalLocalCountry,
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          return true;
                        }}
                        onSecondarySubmit={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'QRadar Landing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA:
                              'Stay here button clicked ' + modalLocalCountry,
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          setToggleCountry(false);
                          return true;
                        }}
                      ></ModalWrapper>
                    ) : null}
                  </div>
                </div>
                <div className="bx--row">
                  <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                    <p className="page_sub_heading">
                      <FormattedMessage {...messages.widgetText_1} />
                    </p>
                    <p className="page_body padding_24_3 mar2">
                      <FormattedMessage {...messages.widgetInfo} />
                    </p>
                    {/*  Product Acquire */}
                    <div className="bx--row ">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 mar5">
                        <Tooltip
                          className=""
                          direction={isSmall ? 'bottom' : 'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.productToAcquire}
                              />
                            </span>
                          }
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Instana Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Acquire tooltip',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        >
                          <span className="slide_text">
                            <FormattedMessage
                              {...messages.productToAcquireHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.productToAcquireDescTooltip}
                            />
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    <div className="bx--row mar1">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <ContentSwitcher
                          selectionMode="manual"
                          onChange={(obj) => {
                            const { name, text } = obj;
                            setProductToAcquire(
                              name == undefined ? '' : name + '',
                            );
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Instana Pricing Page',
                              object: 'Pricing page',
                              resultValue: `CTA clicked ${text}`,
                              CTA: `Acquire product selected`,
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        >
                          {country !== 'CHN' ? (
                            <Switch name={`${Product.SaaS}`} text="SaaS" />
                          ) : (
                            <></>
                          )}
                          <Switch
                            name={`${Product['Self-hosted']}`}
                            text="Self-hosted"
                          />
                        </ContentSwitcher>
                      </div>
                    </div>

                    <div className="bx--row">
                      {/*  Tooltip: Product to acquire */}
                      <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-16">
                        <p className="page_body">
                          <FormattedMessage
                            {...messages.numberOfAPMHostsLabel}
                          />
                        </p>
                      </div>
                      {/*  Tooltip: Number of IQM Hosts */}
                      <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-16">
                        <p className="page_body">
                          <FormattedMessage {...messages.numberIQMHostsLabel} />
                        </p>
                      </div>
                    </div>

                    <div className="bx--row ">
                      {/*  NUmber Input: Product to acquire */}
                      <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-16">
                        <NumberInput
                          id="APMHosts-Id"
                          size="md"
                          ref={apmHostsRef}
                          allowEmpty={true}
                          invalid={invalidAPMHostsMax || invalidAPMHostsMin}
                          helperText={'Minimum purchase of 10'}
                          className="number-input Instana_PE__mar_1rem"
                          min={APMHosts.min}
                          max={APMHosts.max}
                          value={apmHosts}
                          onBlur={() => {
                            null;
                          }}
                          onFocus={() => {
                            setCalculate(false);
                          }}
                          invalidText={
                            invalidAPMHostsMax ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${APMHosts.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidAPMHostsOptimal || invalidAPMHostsMin ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_4}
                                />
                                {` ${APMHosts.optimal} `}
                                <FormattedMessage {...messages.hosts} />
                              </span>
                            ) : null
                          }
                          onChange={() => {
                            setCalculate(false);
                            if (
                              apmHostsRef.current.value === '' ||
                              apmHostsRef.current.value === null
                            ) {
                              setInvalidAPMHostsMin(true);
                              setAPMHosts(APMHosts.min);
                            } else if (
                              Number(apmHostsRef.current.value) > APMHosts.max
                            ) {
                              setAPMHosts(APMHosts.max);
                              setInvalidAPMHostsMax(true);
                            } else if (
                              Number(apmHostsRef.current.value) < APMHosts.min
                            ) {
                              setInvalidAPMHostsMin(true);
                              setAPMHosts(APMHosts.min);
                            } else if (
                              Number(apmHostsRef.current.value) <
                                APMHosts.optimal &&
                              Number(apmHostsRef.current.value) > APMHosts.min
                            ) {
                              setInvalidAPMHostsOptimal(true);
                            } else {
                              setInvalidAPMHostsMax(false);
                              setInvalidAPMHostsMin(false);
                              setInvalidAPMHostsOptimal(false);
                              setCalculate(true);
                              setAPMHosts(Number(apmHostsRef.current.value));
                            }
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Instana Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of APM slider',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                      {/*  Number Input: Number of IQM Hosts */}
                      <div className="bx--col-lg-8 bx--col-md-8 bx--col-sm-16">
                        <NumberInput
                          size="md"
                          ref={iqmHostsRef}
                          allowEmpty={true}
                          className="number-input Instana_PE__mar_1rem"
                          id="IQMHosts-Id"
                          min={IQMHosts.min}
                          max={IQMHosts.max}
                          value={iqmHosts}
                          invalid={invalidIQMHostsMax || invalidIQMHostsMin}
                          helperText={'Minimum purchase of 10'}
                          onBlur={() => {
                            null;
                          }}
                          onFocus={() => {
                            setCalculate(false);
                          }}
                          invalidText={
                            invalidIQMHostsMax == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {APMHosts.max}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidIQMHostsOptimal || invalidIQMHostsMin ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_4}
                                />
                                {` ${APMHosts.optimal} `}
                                <FormattedMessage {...messages.hosts} />
                              </span>
                            ) : null
                          }
                          onChange={() => {
                            setCalculate(false);
                            if (
                              Number(iqmHostsRef.current.value) > IQMHosts.max
                            ) {
                              setIQMHosts(IQMHosts.max);
                              setInvalidIQMHostsMax(true);
                            } else if (
                              Number(iqmHostsRef.current.value) < IQMHosts.min
                            ) {
                              setIQMHosts(IQMHosts.min);
                              setInvalidCIMin(true);
                            } else if (
                              Number(iqmHostsRef.current.value) <
                                IQMHosts.optimal &&
                              Number(iqmHostsRef.current.value) > IQMHosts.min
                            ) {
                              setInvalidIQHostOptimal(true);
                            } else {
                              handleSliders(
                                Number(iqmHostsRef.current.value),
                                setIQMHosts,
                                IQMHosts.max,
                                IQMHosts.min,
                                IQMHosts.optimal,
                              );
                              setInvalidIQMHostsMax(false);
                              setInvalidCIMin(false);
                              setInvalidIQHostOptimal(false);
                              setCalculate(true);
                              setIQMHosts(Number(iqmHostsRef.current.value));
                            }
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'Instana Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of IQM Hosts slider',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>

                    <div id="footnote" className="div-only-l mar3">
                      <div className="bx--row">
                        <div className="bx--col-lg-16 marginleft_1">
                          <div>
                            <Footnote numNote={1} />
                          </div>
                          <div className="footnote_text">
                            <DisclaimerText
                              localeCode={localeCode}
                              product={'Instana'}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bx--col-lg-1"></div>
                  <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16 turbo-space">
                    <div className="bx-row" id="stickyNav">
                      <div className="Instana_PE__notif Instana_PE__notif_mar">
                        {productToAcquire === Product.SaaS ? (
                          <InlineNotification
                            kind="info"
                            role="onprem"
                            title={
                              <FormattedMessage
                                {...messages.inlineTextTitleSaaS}
                              />
                            }
                            subtitle={
                              <FormattedMessage {...messages.inlineTextSaaS} />
                            }
                            statusIconDescription="On-Prem Info"
                            hideCloseButton={true}
                          />
                        ) : (
                          <InlineNotification
                            kind="info"
                            role="oncloud"
                            title={
                              <FormattedMessage
                                {...messages.inlineTextTitleLicense}
                              />
                            }
                            subtitle={
                              <FormattedMessage
                                {...messages.inlineTextLicense}
                              />
                            }
                            statusIconDescription="On-Cloud Info"
                            hideCloseButton={true}
                          />
                        )}
                      </div>
                    </div>
                    <div className="bx-row" id="stickyNav">
                      <div className="Instana_PE__sticky-nav price_table_comp1">
                        {/* Table with results  */}
                        <table className="cds--data-table cds--data-table--sm">
                          <thead>
                            <tr>
                              <th scope="col" colSpan={2}>
                                <div className="price_table_comp__heading">
                                  <FormattedMessage
                                    {...messages.columnOneTilte}
                                  />
                                </div>
                              </th>
                              <th> </th>
                              <th scope="col">
                                <div className="price_table_comp__heading">
                                  <FormattedMessage
                                    {...messages.columnTwoTilte}
                                  />
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody aria-live="polite">
                            <tr>
                              <td colSpan={2}>
                                {' '}
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.numberOfAPMHostsLabelTitle}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">
                                  {`${apmHosts}`}
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.numberOfIQMHostsLabelTitle}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${iqmHosts}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.productToAcquireHeadTooltip}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${productToAcquire}`}</div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        {/* Condition if above ceratin value to contact-us button */}
                        {showContactUs == true ? (
                          <div id="contact_toggle">
                            <p
                              id="widget.contact_info"
                              className="price_table_comp__disclaimer mar4"
                            >
                              {iqmHosts + apmHosts > limits.max ? (
                                <FormattedMessage {...messages.contactInfo2} />
                              ) : (
                                <FormattedMessage {...messages.contactInfo} />
                              )}
                            </p>

                            <ButtonSet className="Instana_PE__stack-button btn-set mar4">
                              <Button
                                id="contactus_pe_btn"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'Instana Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Contact us',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  setFormId(URX_FORM.FORM_ID);
                                  initUrxForm(
                                    environment,
                                    URX_FORM.INSTANCE_ID,
                                    URX_FORM.FORM_ID,
                                    urxlang as IBMLocale,
                                    companyId,
                                  );
                                  setDrawerOpen(true);
                                  setCookie(URX_FORM.FORM_ID);
                                }}
                              >
                                <FormattedMessage {...messages.contactUs} />
                              </Button>
                              &nbsp;
                              <Button
                                size="sm"
                                id="contactus_roi_btn"
                                href="#Instana-BVA"
                                target="_self"
                                rel="noreferrer"
                                kind="secondary"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'Instana Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'View Instana benefits button',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              >
                                <FormattedMessage {...messages.getROIButton} />
                              </Button>
                            </ButtonSet>
                          </div>
                        ) : (
                          //Display Value if condition not met
                          <div id="price_toggle" className="mar4">
                            <p
                              id="widget.estimated-cost"
                              className="Instana_PE__ibm-type-e price_table_comp__bodycopy padding_24_2"
                            >
                              <FormattedMessage
                                {...messages.calculationMessage}
                              />
                              <sup className="Instana_PE__sup">1</sup>
                            </p>
                            <p className="padding_8 price_table_comp__price_lg_bold">
                              {`${estimatedPrice} *`}
                            </p>
                            <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                              * <FormattedMessage {...messages.note} />
                            </p>
                            <ButtonSet className="Instana_PE__stack-button">
                              <Button
                                id="request_pe_btn"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'Instana Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'Request a quote now',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                  setFormId(URX_FORM.FORM_ID);
                                  initUrxForm(
                                    environment,
                                    URX_FORM.INSTANCE_ID,
                                    URX_FORM.FORM_ID,
                                    urxlang as IBMLocale,
                                    companyId,
                                  );
                                  setDrawerOpen(true);
                                  setCookie(URX_FORM.FORM_ID);
                                }}
                              >
                                <FormattedMessage
                                  {...messages.getQuoteButton}
                                />
                              </Button>
                              &nbsp; &nbsp; &nbsp; &nbsp;
                              <Button
                                size="sm"
                                id="request_roi_btn"
                                href="#Instana-BVA"
                                target="_self"
                                rel="noreferrer"
                                kind="secondary"
                                onClick={() => {
                                  segmentTracking('CTA Clicked', {
                                    pageTitle: 'Instana Pricing Page',
                                    object: 'Pricing Page',
                                    resultValue: 'CTA clicked',
                                    CTA: 'View Instana benefits button',
                                    location: 'Estimator',
                                    action: '',
                                    field: '',
                                  });
                                }}
                              >
                                <FormattedMessage {...messages.getROIButton} />
                              </Button>
                            </ButtonSet>
                          </div>
                        )}
                        {/* End display results */}
                      </div>
                    </div>
                    <div id="footnote" className="div-only-media">
                      <div className="bx--row">
                        <div className="bx--col-lg-16 marginleft_1">
                          <div>
                            <Footnote numNote={1} />
                          </div>
                          <div className="footnote_text">
                            <DisclaimerText
                              localeCode={localeCode}
                              product={'Planning Analytics'}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <hr className="planning_analytics__custom-hr" id="hr_row_two" />
              </div>
            </div>
          </div>
        )}
        <FormModal
          pageTitle="Instana PE Page"
          object="Instana PE Page"
          productTitle={'Instana price estimator'}
          pageUrl={''}
          location={'Estimator'}
          bpName={bpName}
          logo={logo}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          setCloseModal={setCloseModal}
          closeModal={closeModal}
          formId={formId}
          lang={lang}
        ></FormModal>
      </div>
    </section>
  );
};

export default InstanaPriceEstimator;
